import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useSelector } from 'react-redux';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';

// API request/response structure for PUT /get-sharpened-images
export type AdjustmentVersion =
  | '6.0.0.enhanced'
  | '7.1.0.enhanced'
  | '7.2.0.enhanced'
  | '7.3.0.enhanced'
  | '7.4.0.enhanced'
  | '7.4.1.enhanced'
  | '7.5.0.enhanced'
  | '7.5.1.enhanced'
  | '7.5.2.enhanced'
  | '7.5.3.enhanced'
  | '7.6.0.enhanced'
  | '8.0.0.enhanced'
  | '8.0.1.enhanced';

export const adjustmentVersions: AdjustmentVersion[] = [
  '6.0.0.enhanced',
  '7.1.0.enhanced',
  '7.2.0.enhanced',
  '7.3.0.enhanced',
  '7.4.0.enhanced',
  '7.4.1.enhanced',
  '7.5.0.enhanced',
  '7.5.1.enhanced',
  '7.5.2.enhanced',
  '7.5.3.enhanced',
  '7.6.0.enhanced',
  '8.0.0.enhanced',
  '8.0.1.enhanced',
];

export interface SharpenedImageRequest {
  imageId: string;
  adjustmentVersion: AdjustmentVersion;
}

export interface SharpenedImageResponse {
  imageId: string;
  s3Path: string;
  adjustmentVersion: AdjustmentVersion;
}

interface UseGetSharpenedImageParams {
  imageId: string | null;
  adjustmentVersion: AdjustmentVersion | null;
}

export const useGetSharpenedImage = ({
  imageId,
  adjustmentVersion,
}: UseGetSharpenedImageParams) => {
  const {
    primaryPracticeId: currentPracticeId,
    primaryOrganizationId: currentOrganizationId,
  } = useSelector((state: any) => state.login.user);

  return useQuery<
    SharpenedImageResponse,
    AxiosError<unknown>,
    SharpenedImageResponse,
    [string, string | null, AdjustmentVersion | null, string, string]
  >(
    [
      endpoints.sharpenedImages,
      imageId,
      adjustmentVersion,
      currentOrganizationId,
      currentPracticeId,
    ],
    async () => {
      const response = await makeAPIRequestReactQuery<SharpenedImageResponse>(
        endpoints.sharpenedImages,
        undefined,
        'POST',
        {
          data: { imageId, adjustmentVersion },
          headers: { org: currentOrganizationId, practice: currentPracticeId },
        },
      );

      return response;
    },
    {
      enabled:
        !!imageId &&
        !!adjustmentVersion &&
        !!currentOrganizationId &&
        !!currentPracticeId,
    },
  );
};
